import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { gsap } from "gsap";

export const HostPanel = () => {
  const wrapRef = useRef(null);
  const mountRef = useRef(null);
  const stateRef = useRef({ active: false, rx: 0, ry: 0 });
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px) and (hover: hover)");
    setIsDesktop(mq.matches);
    const fn = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  useEffect(() => {
    if (!isDesktop) return;
    const mount = mountRef.current;
    const wrap = wrapRef.current;
    if (!mount || !wrap) return;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.1, 50);
    camera.position.set(0, 0.4, 6);

    const gold = new THREE.Color("#4D9FFF");
    const group = new THREE.Group();

    const lineMat = new THREE.LineBasicMaterial({ color: gold, transparent: true, opacity: 0.9 });
    const solidMat = new THREE.MeshStandardMaterial({
      color: gold, metalness: 0.85, roughness: 0.3, transparent: true, opacity: 0.08,
    });

    const body = new THREE.BoxGeometry(2, 1.4, 1.6);
    const roof = new THREE.ConeGeometry(1.55, 1.1, 4);
    const bodyEdges = new THREE.LineSegments(new THREE.EdgesGeometry(body), lineMat);
    const roofEdges = new THREE.LineSegments(new THREE.EdgesGeometry(roof), lineMat);
    roofEdges.rotation.y = Math.PI / 4;
    roofEdges.position.y = 1.25;
    const bodyMesh = new THREE.Mesh(body, solidMat);
    const roofMesh = new THREE.Mesh(roof, solidMat);
    roofMesh.rotation.y = Math.PI / 4;
    roofMesh.position.y = 1.25;
    const door = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(0.5, 0.8, 0.05)), lineMat);
    door.position.set(0, -0.3, 0.81);
    group.add(bodyMesh, roofMesh, bodyEdges, roofEdges, door);
    group.position.y = -3.2;
    scene.add(group);

    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const key = new THREE.PointLight(0xC9A84C, 30, 20);
    key.position.set(2, 3, 4);
    scene.add(key);

    const st = stateRef.current;
    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      group.rotation.x += (st.rx - group.rotation.x) * 0.08;
      group.rotation.y += (st.ry - group.rotation.y) * 0.08;
      renderer.render(scene, camera);
    };
    animate();

    const enter = () => {
      st.active = true;
      gsap.to(group.position, { y: -0.3, duration: 0.6, ease: "power3.out" });
      gsap.to(mount, { opacity: 1, duration: 0.4 });
    };
    const leave = () => {
      st.active = false;
      st.rx = 0; st.ry = 0;
      gsap.to(group.position, { y: -3.2, duration: 0.5, ease: "power2.in" });
      gsap.to(mount, { opacity: 0, duration: 0.5, delay: 0.15 });
    };
    const move = (e) => {
      if (!st.active) return;
      const r = wrap.getBoundingClientRect();
      const nx = (e.clientX - r.left) / r.width - 0.5;
      const ny = (e.clientY - r.top) / r.height - 0.5;
      st.ry = nx * 0.52;
      st.rx = ny * 0.52;
    };
    wrap.addEventListener("mouseenter", enter);
    wrap.addEventListener("mouseleave", leave);
    wrap.addEventListener("mousemove", move);

    const onResize = () => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      wrap.removeEventListener("mouseenter", enter);
      wrap.removeEventListener("mouseleave", leave);
      wrap.removeEventListener("mousemove", move);
      renderer.dispose();
      if (renderer.domElement.parentNode) mount.removeChild(renderer.domElement);
    };
  }, [isDesktop]);

  return (
    <div
      ref={wrapRef}
      data-testid="host-portrait-panel"
      className={`relative w-full bg-[#123059] border border-white/10 overflow-hidden group ${!isDesktop ? "pulse-panel" : ""}`}
      style={{ aspectRatio: "4/5" }}
    >
      <span className="corner-mark tl" /><span className="corner-mark tr" />
      <span className="corner-mark bl" /><span className="corner-mark br" />
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 60%, rgba(77,159,255,0.22), transparent 65%)" }}
      />
      {isDesktop && <div ref={mountRef} className="absolute inset-0 opacity-0 z-10" />}
      <img
        src="/img/plinio.webp"
        alt="Plinio Angulo, Loan Officer, NMLS #1443167"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 group-hover:opacity-30"
      />
      <div className="absolute bottom-4 left-0 right-0 text-center mono text-[9px] tracking-[0.3em] text-white/40 z-10">
        PLINIO ANGULO — HOST
      </div>
    </div>
  );
};
